import type { AdminOrgUser, AdminPrototypeBatch, ConfirmationMethod } from "@/api/types";

/** Home shows "New prototype" for shares younger than this. */
export const RECENT_SHARE_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

export function isRecentShare(createdAt: string, now: Date = new Date()): boolean {
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return now.getTime() - created < RECENT_SHARE_DAYS * DAY_MS;
}

/** Home shows "Batch N confirmed" for this many days after the newest version is confirmed. */
export const RECENT_CONFIRM_DAYS = 7;

export function isRecentConfirmation(confirmedAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!confirmedAt) return false;
  const confirmed = Date.parse(confirmedAt);
  if (Number.isNaN(confirmed)) return false;
  return now.getTime() - confirmed < RECENT_CONFIRM_DAYS * DAY_MS;
}

/** True when the typed access code also appears in the message or link (staff warning only). */
export function codeLeakWarning(code: string, message: string, url: string): boolean {
  const needle = code.trim();
  if (!needle) return false;
  return message.includes(needle) || url.includes(needle);
}

export type RecipientOption = { id: string; name: string; email: string; eligible: boolean };

/** Org users for the "Send email to" checklist. Invited-but-not-joined users are listed disabled. */
export function recipientOptions(users: AdminOrgUser[]): RecipientOption[] {
  return users
    .filter((u) => u.is_active)
    .map((u) => ({ id: u.id, name: u.name, email: u.email, eligible: u.has_password }));
}

/** The version clients see for a batch: the newest one that is not archived. */
export function currentVersion<T extends { version: number; archived_at?: string | null }>(versions: T[]): T | undefined {
  return versions.reduce<T | undefined>(
    (best, v) => (v.archived_at || (best && best.version >= v.version) ? best : v),
    undefined,
  );
}

/** The number a new batch gets by default: one above the highest, or 1. */
export function nextBatchNumber(batches: { number: number }[]): number {
  return Math.max(0, ...batches.map((b) => b.number)) + 1;
}

/** The only version number the API accepts for a batch's next version (archived ones count). */
export function nextVersion(batch: { versions: { version: number }[] }): number {
  return Math.max(0, ...batch.versions.map((v) => v.version)) + 1;
}

/** True when the newest batch has a current version the client has not confirmed (a warning, not a lock). */
export function latestBatchUnconfirmed(batches: AdminPrototypeBatch[]): boolean {
  const newest = batches.reduce<AdminPrototypeBatch | undefined>((best, b) => (best && best.number >= b.number ? best : b), undefined);
  const current = newest ? currentVersion(newest.versions) : undefined;
  return Boolean(current && !current.confirmed_at);
}

/** The ways staff can record that an owner confirmed outside the portal, in the order offered. */
export const CONFIRMATION_METHODS = ["phone", "whatsapp", "in_person", "email"] as const satisfies readonly ConfirmationMethod[];

/** Owners who can confirm a version: joined and active. */
export function confirmingOwners(users: AdminOrgUser[]): AdminOrgUser[] {
  return users.filter((u) => u.role === "owner" && u.has_password && u.is_active);
}

/** True when a batch name already says "Batch N", so a heading need not repeat the number. */
export function nameRepeatsNumber(name: string, number: number): boolean {
  return new RegExp(`^batch\\s*${number}(?!\\d)`, "i").test(name.trim());
}
